import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Skeleton, notification } from 'antd';
import {
    CaretLeft,
    CaretRight,
    CheckCircle,
    Phone,
    UserCircle,
    UsersThree,
    IdentificationBadge,
    Scroll,
    UserPlus,
} from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import opportunityInfoService from '../../../../services/oneButtonServices/opportunityInfoService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { ContactManageModal, type ContactFormState, type ValidationErrors } from './ContactManageModal';
import type { ContactManageModalProps } from './ContactManageModal';
import {
    buildUpdatedCustomerListForSave,
    buildContactValidationAlert,
    hasUnsavedContactChanges,
    validateAllContactForms,
    validateContactBusinessRules,
    type ContactValidationAlert,
} from '../../../../helpers/contactFormValidation';
import { TitleInfoModal } from './TitleInfoModal';
import {
    opportunityHasSyncedTitleContact,
} from '../../../../helpers/titleContactStatus';
import {
    contactHasPassedCredit,
    getOpportunityPrimaryFinancePartner,
} from './contactCreditStatus';

export interface ContactsSnapshotCardProps {
    loading?: boolean;
    opportunityId?: string | null;
    isTitleContactExist?: boolean | null;
    onAddTitleContactsClick?: () => void;
    onBlackKnightTitleCheckClick?: () => void;
    /** Split modal: contact list left, edit form right (no nested contact modal) */
    layout?: 'card' | 'modalSplit';
}

/** Tag row styling: PRIMARY = accent; role labels = muted caps; Credit Passed = success */
export type ContactSnapshotTagTone = 'primary' | 'neutral' | 'success';

export interface ContactSnapshotTag {
    key: string;
    label: string;
    tone: ContactSnapshotTagTone;
}

/** Credit Passed chip shown next to a contact name when any product status is Passed. */
function CreditPassedFlag() {
    return (
        <span className="obd-credit-passed-flag" aria-label="Credit Passed">
            <CheckCircle size={12} weight="fill" aria-hidden />
            Credit Passed
        </span>
    );
}

function getContactDisplay(
    c: any,
    override?: Partial<ContactFormState>,
    options?: { sunlightCreditStatus?: any; isSunlightPartnerExist?: boolean; primaryFinancePartner?: string },
) {
    const firstName = override?.firstName ?? c?.FirstName ?? '';
    const lastName = override?.lastName ?? c?.LastName ?? '';
    const name = `${firstName} ${lastName}`.trim() || 'No name';
    const phone = override?.phone ?? c?.Phone ?? '';
    const tags: ContactSnapshotTag[] = [];

    const isPrimary = override?.isPrimaryContact ?? !!(c?.IsPrimaryContact || c?.isPrimaryContact);
    if (isPrimary) tags.push({ key: 'primary', label: 'PRIMARY', tone: 'primary' });

    const isSecondary = override?.isSecondaryContact ?? !!(c?.IsSecondaryContact || c?.isSecondaryContact);
    if (isSecondary) tags.push({ key: 'secondary', label: 'SECONDARY CONTACT', tone: 'neutral' });

    const onUtility = override?.onUtilityBill ?? !!(c?.IsUtilityBillOwner || c?.isUtilityBillOwner);
    if (onUtility) tags.push({ key: 'utility', label: 'ON UTILITY', tone: 'neutral' });

    const onTitle = override?.onTitle ?? !!(c?.OnTitleCheck || c?.onTitleCheck);
    const isHO = onTitle ? false : !!(override?.homeowner ?? (c?.IsHomeowner || c?.isHomeowner));

    if (isHO) tags.push({ key: 'homeowner', label: 'HOMEOWNER', tone: 'neutral' });
    if (onTitle) tags.push({ key: 'title', label: 'ON TITLE', tone: 'neutral' });

    const creditPassed = contactHasPassedCredit(c, isPrimary, options);

    return { name, phone, tags, creditPassed, firstName, lastName };
}

function contactInitials(firstName: string, lastName: string): string {
    const ini = `${String(firstName).trim()[0] ?? ''}${String(lastName).trim()[0] ?? ''}`.toUpperCase();
    return ini || '?';
}

/** Modal list + detail hero: only primary / secondary role labels. */
function getPrimaryOrSecondaryTag(tags: ContactSnapshotTag[]): ContactSnapshotTag | undefined {
    return tags.find((t) => t.key === 'primary') ?? tags.find((t) => t.key === 'secondary');
}

const MODAL_HERO_TAG_ORDER = ['primary', 'secondary', 'utility', 'homeowner', 'title'] as const;

/** Detail hero: primary/secondary plus other active contact-type flags. */
function getHeroDisplayTags(tags: ContactSnapshotTag[]): ContactSnapshotTag[] {
    return MODAL_HERO_TAG_ORDER.map((key) => tags.find((t) => t.key === key)).filter(
        (t): t is ContactSnapshotTag => !!t,
    );
}

/** Short labels for V2 split modal list / hero (not full card tag row). */
function formatModalRoleLabel(tag: ContactSnapshotTag): string {
    if (tag.key === 'primary') return 'Primary';
    if (tag.key === 'secondary') return 'Secondary';
    if (tag.key === 'utility') return 'On utility';
    if (tag.key === 'homeowner') return 'Homeowner';
    if (tag.key === 'title') return 'On title';
    return tag.label;
}

/**
 * Contacts + Lead & Title on one card: contact rows, lead fields, footer with AHJ / office and actions.
 */
export const ContactsSnapshotCard: React.FC<ContactsSnapshotCardProps> = ({
    loading: propsLoading = false,
    opportunityId,
    isTitleContactExist = null,
    onAddTitleContactsClick,
    onBlackKnightTitleCheckClick,
    layout = 'card',
}) => {
    const isModalSplit = layout === 'modalSplit';
    const [contactOverrides, setContactOverrides] = useState<Record<number, Partial<ContactFormState>>>({});
    const [contactListErrors, setContactListErrors] = useState<Record<number, ValidationErrors>>({});
    const [listValidationSubmitted, setListValidationSubmitted] = useState(false);
    const [validationAlert, setValidationAlert] = useState<ContactValidationAlert | null>(null);
    const [modalContactIndex, setModalContactIndex] = useState<number | null>(null);
    const dashboardContactsOpenNewContact = useOneButtonStore((s) => s.dashboardContactsOpenNewContact);
    const setDashboardContactsOpenNewContact = useOneButtonStore((s) => s.setDashboardContactsOpenNewContact);
    const contactsTitleSyncRevision = useOneButtonStore((s) => s.contactsTitleSyncRevision);

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setQuotesRefreshTrigger = useOneButtonStore((s) => s.setQuotesRefreshTrigger);
    const customers: any[] = opportunityDetails?.Customers ?? [];
    const titleContactSynced = opportunityHasSyncedTitleContact(opportunityDetails);

    const [sunlightCreditStatus, setSunlightCreditStatus] = useState<any>(null);

    const isSunlightPartnerExist = useMemo(() => {
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        return products.some((p: any) => p.Finance_Partner === 'Sunlight' || p.Finance_Partner_Display_Name === 'Sunlight');
    }, [opportunityDetails]);

    const primaryFinancePartner = useMemo(
        () => getOpportunityPrimaryFinancePartner(opportunityDetails),
        [opportunityDetails],
    );

    useEffect(() => {
        const fetchSunlight = async () => {
            if (!opportunityDetails?.Opportunity_Id || !isSunlightPartnerExist) {
                setSunlightCreditStatus(null);
                return;
            }
            try {
                const res = await opportunityInfoService.getSunlightCreditStatus(opportunityDetails.Opportunity_Id);
                if (res && res.length) {
                    setSunlightCreditStatus(res[0]);
                } else {
                    setSunlightCreditStatus(null);
                }
            } catch {
                setSunlightCreditStatus(null);
            }
        };
        fetchSunlight();
    }, [opportunityDetails?.Opportunity_Id, isSunlightPartnerExist]);

    const [saving, setSaving] = useState(false);
    const loading = propsLoading || saving;
    const theme = useOneButtonStore((s) => s.theme);
    const titleInfo = useOneButtonStore((s) => s.titleInfo) as Record<string, unknown> | null;

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const opportunityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Opportunity_Information_Access');
    const isOpportunityInfoDisabled = opportunityInfoAccess !== 'Edit' && opportunityInfoAccess !== 'View';
    const [titleInfoModalOpen, setTitleInfoModalOpen] = useState(false);
    const [noticeAPI, contextHolder] = notification.useNotification();


    const highlight = opportunityDetails?.OpportunityHighlight ?? {};
    const leadSource = (highlight as { LeadSource?: string }).LeadSource ?? '';
    const ahj = (highlight as { AHJ?: string }).AHJ ?? '';
    const managingOffice = (highlight as { Managing_Office?: string }).Managing_Office ?? '';

    // -- Helpers for optimization --
    const showNotice = useCallback((type: 'success' | 'error' | 'warning' | 'info', message?: string, description?: string) => {
        noticeAPI[type]({
            message,
            description,
            placement: 'topRight',
            className: `obd-notification obd-theme-${theme}`,
            style: { zIndex: 10000 },
        });
    }, [noticeAPI, theme]);

    const refreshOpportunityData = useCallback(async () => {
        if (!opportunityDetails?.SalesForce_Id) return;
        try {
            const updatedDetails = await opportunityInfoService.getOpportunityDetail(opportunityDetails.SalesForce_Id);
            const actualData = updatedDetails?.data ?? updatedDetails;
            // `getOpportunityDetail` can return `null` (e.g. 400 from API for "no data").
            // Avoid clearing the store, which makes the UI look stale/blank after a successful save.
            if (actualData) {
                setOpportunityDetails(actualData);
                setContactOverrides({});
                setContactListErrors({});
                setListValidationSubmitted(false);
                setValidationAlert(null);
            }
        } catch (err) {
            console.error("Failed to refresh opportunity data", err);
        }
    }, [opportunityDetails?.SalesForce_Id, setOpportunityDetails]);

    const mergedCustomers = useMemo(() => {
        const isLightreach = opportunityDetails?.opportunityProduct?.[0]?.Finance_Partner === 'Lightreach';
        
        let primaryFound = false;

        return customers.map((c, i) => {
            const override = contactOverrides[i];
            
            // a) Determine Primary Role
            let isThisPrimary = false;
            // Check override first, then original
            if (override?.isPrimaryContact !== undefined) {
                isThisPrimary = override.isPrimaryContact;
            } else {
                isThisPrimary = !!(c.IsPrimaryContact || c.isPrimaryContact);
            }

            // Enforce single primary rule
            if (isThisPrimary) {
                if (primaryFound) isThisPrimary = false;
                else primaryFound = true;
            }

            // b) Determine Secondary Role (Secondary if original was secondary and not now primary)
            const originalSecondary = !!(c.IsSecondaryContact || c.isSecondaryContact);
            const isThisSecondary = override?.isSecondaryContact !== undefined 
                ? override.isSecondaryContact 
                : (originalSecondary && !isThisPrimary);

            // c) Determine AddToContract Role
            const originalContract = !!(c.AddToContract || c.addToContract);
            let isThisContract = override?.addToContract !== undefined
                ? override.addToContract
                : originalContract;

            const onTitle = override?.onTitle ?? !!(c?.OnTitleCheck || c?.onTitleCheck);

            return { 
                ...c, 
                // Force sync role flags so Modal and Display both see consistent state
                IsPrimaryContact: isThisPrimary,
                isPrimaryContact: isThisPrimary,
                IsSecondaryContact: isThisSecondary,
                isSecondaryContact: isThisSecondary,
                AddToContract: isThisContract,
                addToContract: isThisContract,
                OnTitleCheck: onTitle,
                onTitleCheck: onTitle,
                _override: override,
            };
        });
    }, [customers, contactOverrides, opportunityDetails, contactsTitleSyncRevision]);

    const hasUnsavedChanges = useMemo(
        () => hasUnsavedContactChanges(customers, contactOverrides),
        [customers, contactOverrides],
    );

    useEffect(() => {
        if (!isModalSplit || !dashboardContactsOpenNewContact) return;
        setModalContactIndex(customers.length);
        setDashboardContactsOpenNewContact(false);
    }, [
        isModalSplit,
        dashboardContactsOpenNewContact,
        customers.length,
        setDashboardContactsOpenNewContact,
    ]);

    useEffect(() => {
        if (!isModalSplit || propsLoading) return;
        if (mergedCustomers.length === 0) {
            setModalContactIndex(null);
            return;
        }
        setModalContactIndex((idx) => {
            if (idx === null || idx >= mergedCustomers.length) return 0;
            return idx;
        });
    }, [isModalSplit, mergedCustomers.length, propsLoading]);

    useEffect(() => {
        if (contactsTitleSyncRevision === 0) return;
        setContactOverrides({});
        setContactListErrors({});
        setListValidationSubmitted(false);
        setValidationAlert(null);
    }, [contactsTitleSyncRevision]);

    const handleSplitFormChange = useCallback((data: ContactFormState) => {
        setContactOverrides((prev) => {
            if (modalContactIndex === null) return prev;
            const merged = { ...prev[modalContactIndex], ...data };
            if (JSON.stringify(prev[modalContactIndex]) === JSON.stringify(merged)) return prev;
            const nextOverrides = { ...prev, [modalContactIndex]: merged };
            if (listValidationSubmitted) {
                const nextErrors = validateAllContactForms(customers, nextOverrides, modalContactIndex, data);
                setContactListErrors(nextErrors);
                if (Object.keys(nextErrors).length === 0) {
                    setValidationAlert(null);
                }
            }
            return nextOverrides;
        });
    }, [modalContactIndex, listValidationSubmitted, customers]);

    const handleSaveContact: ContactManageModalProps['onSave'] = async (index: number, data: ContactFormState) => {
        if (!opportunityDetails?.SalesForce_Id) return;

        const allErrors = validateAllContactForms(customers, contactOverrides, index, data);
        setContactListErrors(allErrors);
        setListValidationSubmitted(true);

        if (Object.keys(allErrors).length > 0) {
            setValidationAlert(buildContactValidationAlert(allErrors, customers, contactOverrides));
            return;
        }

        const businessRuleIssue = validateContactBusinessRules(customers, contactOverrides, index, data);
        if (businessRuleIssue) {
            setValidationAlert(businessRuleIssue);
            return;
        }

        setValidationAlert(null);

        try {
            setSaving(true);

            const updatedCustomerList = buildUpdatedCustomerListForSave(
                customers,
                contactOverrides,
                index,
                data,
            );

            const payload = {
                SalesForce_Id: opportunityDetails.SalesForce_Id,
                opportunityId: opportunityDetails.Opportunity_Id,
                InstallationAddress: opportunityDetails.InstallationAddress ? {
                    street: opportunityDetails.InstallationAddress.Street || '',
                    city: opportunityDetails.InstallationAddress.City || '',
                    state: opportunityDetails.InstallationAddress.State || opportunityDetails.InstallationAddress.stateCode || '',
                    zipCode: opportunityDetails.InstallationAddress.ZipCode || opportunityDetails.InstallationAddress.zipCode || '',
                    latitude: opportunityDetails.InstallationAddress.Latitude || 0,
                    longitude: opportunityDetails.InstallationAddress.Longitude || 0
                } : null,
                customerInformation: updatedCustomerList
            };

            const response = await opportunityInfoService.updateContactInformation(payload);
            const isSuccess = response?.Status === 0 || response?.status === 0 || !response?.Status;

            if (isSuccess) {
                // Update the UI immediately with our local list (so the user sees changes even if
                // the follow-up OpportunityDetail call is delayed / returns null transiently).
                setOpportunityDetails({
                    ...(opportunityDetails ?? {}),
                    Customers: updatedCustomerList,
                });
                showNotice('success', index === customers.length ? 'Contact Created' : 'Contact Updated', 'The changes have been saved successfully.');
                setContactOverrides({});
                setContactListErrors({});
                setListValidationSubmitted(false);
                setValidationAlert(null);
                if (!isModalSplit) setModalContactIndex(null);
                await refreshOpportunityData();
                runContractSaveUpdateLoanStatusFlow();

            } else {
                showNotice('error', 'Save Error', response?.Message || 'Failed to save contact information.');
            }
        } catch (error) {
            console.error("Failed to update contact information", error);
            showNotice('error', 'Unexpected Error', 'An error occurred while saving. Please try again.');
            // Fallback to local override if API fails
            setContactOverrides(prev => ({ ...prev, [index]: data }));
            if (!isModalSplit) setModalContactIndex(null);
        } finally {
            setSaving(false);
        }
    };

    const runContractSaveUpdateLoanStatusFlow = async () =>{
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct;
        let haveGoodLeapProduct = false;
        let haveLightreachProduct = false;
        (products || []).forEach((product: any) => {
            if (product.ActiveFlag && product.Finance_Partner.toLowerCase() === 'goodleap') haveGoodLeapProduct = true;
            if (product.Finance_Partner.toLowerCase() === 'lightreach') haveLightreachProduct = true;
        });
        if (!haveGoodLeapProduct && !haveLightreachProduct) return;
        try {
            await quoteServices.contractUpdateLoanStatus(opportunityDetails.Opportunity_Id, 'Contact', 0);
        } catch {
            return;
        }
        try {
            const data = await quoteServices.contractGetLoanStatusByOpportunity(opportunityDetails.Opportunity_Id);
            setQuotesRefreshTrigger(Date.now());
            if (Array.isArray(data) && data.some((detail: any) => detail.Is_Change_Order)) {
                if (!haveLightreachProduct) {
                    showNotice('error', 'Unexpected Error', 'New quote is required due to changes in utility information before change order');
                };
            }
        } catch {
            /* getLoanStatus failure: Angular only clears loader */
        }
    }

    const handleDeleteContact = async (index: number) => {
        const c = mergedCustomers[index];
        const contactId = c?.Id || c?.CustomerId;
        const displayName = `${c?.FirstName || ''} ${c?.LastName || ''}`.trim() || 'Contact';

        if (!contactId || !opportunityDetails?.SalesForce_Id) {
            showNotice('error', 'Error', 'Cannot delete contact: missing ID.');
            return;
        }

        try {
            setSaving(true);
            const response = await opportunityInfoService.removeContact(String(contactId));
            const isSuccess = response?.Status === 0 || response?.status === 0 || !response?.Status;

            if (isSuccess) {
                showNotice('success', 'Contact Removed', `${displayName} was successfully removed.`);
                await refreshOpportunityData();
            } else {
                showNotice('error', 'Delete Error', response?.Message || 'Failed to delete contact.');
            }
        } catch (error: any) {
            console.error("Failed to delete contact", error);
            const errorMessage = error?.response?.data?.Message || error?.response?.data?.message || error?.message || 'Deletion Failed. Please try again.';
            showNotice('error', 'Delete Error', errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const contactRows = (
        <div className="obd-contacts-body">
            {loading ? (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
            ) : mergedCustomers.length === 0 ? (
                <div className="obd-contact-cards-empty">No contacts</div>
            ) : (
                <div className="obd-contact-rows" role="list">
                    {mergedCustomers.map((customer, index) => {
                        const display = getContactDisplay(customer, customer._override, {
                            sunlightCreditStatus,
                            isSunlightPartnerExist,
                            primaryFinancePartner,
                        });
                        const roleTags = display.tags;
                        const isSelected = isModalSplit && modalContactIndex === index;
                        const initials = contactInitials(display.firstName, display.lastName);

                        if (isModalSplit) {
                            const listRoleTag = getPrimaryOrSecondaryTag(roleTags);
                            const rowHasErrors =
                                listValidationSubmitted && !!contactListErrors[index];
                            return (
                                <button
                                    key={`${index}-${customer.OnTitleCheck ?? ''}-${contactsTitleSyncRevision}`}
                                    type="button"
                                    className={`obd-contact-row obd-contact-row--modal${isSelected ? ' obd-contact-row--selected' : ''}${rowHasErrors ? ' obd-contact-row--invalid' : ''}`}
                                    onClick={() => setModalContactIndex(index)}
                                    role="listitem"
                                    aria-label={`View or edit ${display.name}`}
                                    aria-current={isSelected ? 'true' : undefined}
                                >
                                    <span className="obd-contact-row__avatar" aria-hidden>
                                        {initials}
                                    </span>
                                    <span className="obd-contact-row__stack">
                                        <span className="obd-contact-row__name-line">
                                            <span className="obd-contact-row__name">{display.name}</span>
                                            {display.creditPassed ? <CreditPassedFlag /> : null}
                                        </span>
                                        {display.phone ? (
                                            <span className="obd-contact-row__subline">
                                                <span className="obd-contact-row__phone-inline">{display.phone}</span>
                                            </span>
                                        ) : null}
                                    </span>
                                    {(listRoleTag || rowHasErrors) ? (
                                        <span className="obd-contact-row__meta-col">
                                            {listRoleTag ? (
                                                <span
                                                    className={`obd-contact-row__pill obd-contact-row__pill--rail obd-contact-row__pill--${listRoleTag.tone}`}
                                                >
                                                    {formatModalRoleLabel(listRoleTag)}
                                                </span>
                                            ) : null}
                                            {rowHasErrors ? (
                                                <span className="obd-contact-row__pill obd-contact-row__pill--rail obd-contact-row__pill--invalid">
                                                    Required
                                                </span>
                                            ) : null}
                                        </span>
                                    ) : null}
                                    <CaretRight
                                        size={14}
                                        weight="bold"
                                        className="obd-contact-row__chevron"
                                        aria-hidden
                                    />
                                </button>
                            );
                        }

                        const rowColumns = (
                            <>
                                <div className="obd-contact-row__left">
                                    <span className="obd-contact-row__name-line">
                                        <span className="obd-contact-row__name">{display.name}</span>
                                        {display.creditPassed ? <CreditPassedFlag /> : null}
                                    </span>
                                    {roleTags.length > 0 ? (
                                        <div
                                            className="obd-contact-row__tags-row"
                                            role="list"
                                            aria-label="Contact roles"
                                        >
                                            {roleTags.map((tag, tagIndex) => (
                                                <span
                                                    key={`${tag.key}-${tagIndex}`}
                                                    className={`obd-contact-row__tag obd-contact-row__tag--${tag.tone}`}
                                                    role="listitem"
                                                >
                                                    {tag.label}
                                                </span>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="obd-contact-row__right">
                                    <div className="obd-contact-row__right-stack">
                                        <span className="obd-contact-row__phone">
                                            <Phone size={12} weight="regular" aria-hidden />
                                            {display.phone || '—'}
                                        </span>
                                    </div>
                                </div>
                            </>
                        );
                        return (
                            <button
                                key={index}
                                type="button"
                                className={`obd-contact-row${isSelected ? ' obd-contact-row--selected' : ''}`}
                                onClick={() => setModalContactIndex(index)}
                                role="listitem"
                                aria-label={`View or edit ${display.name}`}
                                aria-current={isSelected ? 'true' : undefined}
                            >
                                {isModalSplit ? (
                                    <span className="obd-contact-row__avatar" aria-hidden>
                                        {initials}
                                    </span>
                                ) : null}
                                {isModalSplit ? (
                                    <div className="obd-contact-row__main">{rowColumns}</div>
                                ) : (
                                    rowColumns
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const contactsFooter = (
        <footer className="obd-dashboard-main-card__foot obd-contacts-footer obd-contacts-footer--merged">
            <div className="obd-contacts-footer__meta" aria-label="Jurisdiction and office">
                <div className="obd-contacts-footer__meta-item">
                    <span className="obd-contacts-footer__meta-label">AHJ</span>
                    <span className="obd-contacts-footer__meta-value">{ahj || '—'}</span>
                </div>
                <div className="obd-contacts-footer__meta-item">
                    <span className="obd-contacts-footer__meta-label">Managing Office</span>
                    <span className="obd-contacts-footer__meta-value">{managingOffice || '—'}</span>
                </div>
            </div>
            <div
                className="obd-contacts-footer__actions obd-metrics__action-group"
                role="group"
                aria-label="Contact actions"
            >
                <button
                    type="button"
                    className="obd-merged-system__chip-btn"
                    onClick={() => setTitleInfoModalOpen(true)}
                    aria-label="Open title information"
                >
                    <Scroll size={16} weight="bold" aria-hidden />
                    <span>Title Info</span>
                </button>
                {isTitleContactExist !== null && !titleContactSynced ? (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn"
                        aria-label="Add title contacts"
                        onClick={
                            isTitleContactExist === true
                                ? onAddTitleContactsClick
                                : onBlackKnightTitleCheckClick
                        }
                    >
                        <IdentificationBadge size={16} weight="bold" aria-hidden />
                        <span>Add Title Contacts</span>
                    </button>
                ) : null}
                <button
                    type="button"
                    className="obd-merged-system__chip-btn"
                    onClick={() => setModalContactIndex(customers.length)}
                    aria-label="Add contact"
                >
                    <UserPlus size={16} weight="bold" aria-hidden />
                    <span>Add Contact</span>
                </button>
            </div>
        </footer>
    );

    const contactEditor =
        modalContactIndex !== null ? (
            <ContactManageModal
                key={`${modalContactIndex}-${contactsTitleSyncRevision}`}
                open
                presentation={isModalSplit ? 'panel' : 'modal'}
                onClose={() => setModalContactIndex(null)}
                contact={
                    modalContactIndex < mergedCustomers.length ? mergedCustomers[modalContactIndex] : null
                }
                contactIndex={modalContactIndex ?? 0}
                initialOverride={
                    modalContactIndex !== null && modalContactIndex < customers.length
                        ? contactOverrides[modalContactIndex]
                        : undefined
                }
                onSave={handleSaveContact}
                onDelete={handleDeleteContact}
                onDeleteNew={() => setModalContactIndex(0)}
                onNotice={showNotice}
                contactOverrides={contactOverrides}
                hasUnsavedChanges={hasUnsavedChanges}
                listValidationSubmitted={listValidationSubmitted}
                listValidationErrors={
                    modalContactIndex !== null ? contactListErrors[modalContactIndex] : undefined
                }
                validationAlert={validationAlert}
                onValidationAlertDismiss={() => setValidationAlert(null)}
                onFormChange={isModalSplit && modalContactIndex !== null ? handleSplitFormChange : undefined}
            />
        ) : null;

    if (isModalSplit) {
        const detailOpen = modalContactIndex !== null;
        const activeDetailDisplay =
            modalContactIndex !== null && modalContactIndex < mergedCustomers.length
                ? getContactDisplay(mergedCustomers[modalContactIndex], mergedCustomers[modalContactIndex]._override, {
                      sunlightCreditStatus,
                      isSunlightPartnerExist,
                      primaryFinancePartner,
                  })
                : null;
        const detailHeading =
            modalContactIndex === null
                ? null
                : activeDetailDisplay?.name ?? 'New contact';
        const detailInitials =
            activeDetailDisplay != null
                ? contactInitials(activeDetailDisplay.firstName, activeDetailDisplay.lastName)
                : '+';
        const heroDisplayTags = activeDetailDisplay ? getHeroDisplayTags(activeDetailDisplay.tags) : [];
        const heroSubline =
            modalContactIndex !== null && modalContactIndex >= mergedCustomers.length
                ? 'New contact'
                : activeDetailDisplay?.phone || null;

        return (
            <div
                className={`obd-contacts-modal-split obd-theme-${theme}${detailOpen ? ' obd-contacts-modal-split--detail-open' : ''} ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`}
            >
                {contextHolder}
                <div className="obd-contacts-modal-split__list">
                    <div className="obd-contacts-modal-split__list-rail">
                        <div className="obd-contacts-modal-split__list-head">
                            <h3 className="obd-contacts-v2__title obd-contacts-v2__title--with-icon">
                                <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                                    <UsersThree size={16} weight="duotone" />
                                </span>
                                Contacts
                            </h3>
                            <div className="obd-contacts-modal-split__list-head-actions">
                                {!loading ? (
                                    <span className="obd-contacts-modal-split__count">
                                        {mergedCustomers.length}
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    className="obd-contacts-modal-split__add-btn"
                                    onClick={() => setModalContactIndex(customers.length)}
                                    aria-label="Add contact"
                                >
                                    <UserPlus size={15} weight="bold" aria-hidden />
                                    <span>Add</span>
                                </button>
                            </div>
                        </div>
                        <div className="obd-contacts-modal-split__list-scroll obd-scroll-minimal">
                            {contactRows}
                        </div>
                    </div>
                </div>
                <div className="obd-contacts-modal-split__detail">
                    <div className="obd-contacts-modal-split__detail-panel">
                        {detailOpen ? (
                            <div className="obd-contacts-modal-split__detail-toolbar">
                                <button
                                    type="button"
                                    className="obd-contacts-modal-split__back"
                                    onClick={() => setModalContactIndex(null)}
                                >
                                    <CaretLeft size={14} weight="bold" aria-hidden />
                                    <span>All contacts</span>
                                </button>
                                {detailHeading ? (
                                    <span className="obd-contacts-modal-split__detail-heading">{detailHeading}</span>
                                ) : null}
                            </div>
                        ) : null}
                        {modalContactIndex !== null ? (
                            <div className="obd-contacts-modal-split__detail-editor">
                                <div className="obd-contacts-modal-split__detail-hero" aria-hidden={false}>
                                    <span className="obd-contacts-modal-split__detail-hero-avatar">{detailInitials}</span>
                                    <div className="obd-contacts-modal-split__detail-hero-copy">
                                                    <div className="obd-contacts-modal-split__detail-hero-head">
                                                        <span className="obd-contacts-modal-split__detail-hero-name">
                                                            {detailHeading}
                                                        </span>
                                                        {activeDetailDisplay?.creditPassed ? <CreditPassedFlag /> : null}
                                                    </div>
                                                    <div className="obd-contacts-modal-split__detail-hero-meta">
                                                        {heroSubline ? (
                                                            <span className="obd-contacts-modal-split__detail-hero-tags obd-contacts-modal-split__detail-hero-tags--muted">
                                                                {heroSubline}
                                                            </span>
                                                        ) : <span />}
                                                        {heroDisplayTags.length > 0 ? (
                                                            <div
                                                                className="obd-contacts-modal-split__detail-hero-tags-row"
                                                                role="list"
                                                                aria-label="Contact roles"
                                                            >
                                                                {heroDisplayTags.map((t) => (
                                                                    <span
                                                                        key={t.key}
                                                                        className={`obd-contacts-modal-split__detail-hero-tag obd-contacts-modal-split__detail-hero-tag--${t.tone}`}
                                                                        role="listitem"
                                                                    >
                                                                        {formatModalRoleLabel(t)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                    </div>
                                </div>
                                {contactEditor}
                            </div>
                        ) : (
                            <div className="obd-contacts-modal-split__detail-empty">
                                <span className="obd-contacts-modal-split__detail-empty-icon" aria-hidden>
                                    <UserCircle size={32} weight="duotone" />
                                </span>
                                <p className="obd-contacts-modal-split__detail-empty-title">Select a contact</p>
                                <p className="obd-contacts-modal-split__detail-empty-hint">
                                    Pick someone from the list to view or edit their details, or add a new contact
                                    below.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`obd-card obd-card--contacts obd-card--contacts-v2 obd-dashboard-main-card ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`}>
            {contextHolder}
            <header className="obd-dashboard-main-card__head">
                <div className="obd-contacts-v2__header-row">
                    <h3 className="obd-contacts-v2__title obd-contacts-v2__title--with-icon">
                        <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                            <UsersThree size={18} weight="duotone" />
                        </span>
                        Contacts
                    </h3>
                    <div className="obd-contacts-v2__header-lead">
                        <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                            <IdentificationBadge size={18} weight="duotone" />
                        </span>
                        <div className="obd-contacts-v2__header-lead-text">
                            <span className="obd-contacts-v2__lead-label">Lead &amp; Title</span>
                            <span className="obd-contacts-v2__lead-primary">{leadSource || '—'}</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="obd-dashboard-main-card__body">{contactRows}</div>

            {contactsFooter}

            <TitleInfoModal
                open={titleInfoModalOpen}
                onClose={() => setTitleInfoModalOpen(false)}
                raw={titleInfo}
                theme={theme}
            />

            {contactEditor}
        </div>
    );
};

export default ContactsSnapshotCard;
